// @ts-check
/** @typedef {import('../types/api').Expense} Expense */
/** @typedef {import('../types/api').ExpenseSummary} ExpenseSummary */
/** @typedef {import('../types/api').TaxReport} TaxReport */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const expenseService = {
  /**
   * @param {Object} [filters]
   * @returns {Promise<Expense[]>}
   */
  async getExpenses(filters = {}) {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.vendor_id) params.append('vendor_id', filters.vendor_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.include_deleted) params.append('include_deleted', 'true');

    const data = await api.get(`/api/admin/expenses?${params}`);
    assertArray(data, 'expenseService.getExpenses');
    return data;
  },

  /**
   * @param {string} expenseId
   * @returns {Promise<Expense>}
   */
  async getExpense(expenseId) {
    const data = await api.get(`/api/admin/expenses/${expenseId}`);
    assertShape(data, ['id'], 'expenseService.getExpense');
    return data;
  },

  /**
   * @param {string} expenseId
   * @returns {Promise<Object>}
   */
  async getExpenseVendorOrder(expenseId) {
    // Backend never includes an `id` key here — success returns
    // {expense_id, vendor_order_id, ...} and several valid "not linked"/
    // "not found" states return only {message}. assertShape(['id']) threw on
    // every response, so the caller's vendor-order link never opened and
    // even the friendly "not linked" toast surfaced as a generic error.
    return api.get(`/api/admin/expenses/${expenseId}/vendor-order`);
  },

  /**
   * @param {Object} expenseData
   * @returns {Promise<Expense>}
   */
  async createExpense(expenseData) {
    return api.post('/api/admin/expenses', expenseData);
  },

  /**
   * @param {string} expenseId
   * @param {Object} expenseData
   * @returns {Promise<Expense>}
   */
  async updateExpense(expenseId, expenseData) {
    return api.put(`/api/admin/expenses/${expenseId}`, expenseData);
  },

  /**
   * @param {string} expenseId
   * @param {boolean} [hardDelete=false]
   * @returns {Promise<void>}
   */
  async deleteExpense(expenseId, hardDelete = false) {
    const params = hardDelete ? '?hard_delete=true' : '';
    return api.del(`/api/admin/expenses/${expenseId}${params}`);
  },

  /**
   * @param {string} expenseId
   * @returns {Promise<Expense>}
   */
  async restoreExpense(expenseId) {
    return api.post(`/api/admin/expenses/${expenseId}/restore`);
  },

  /**
   * @param {string} [startDate]
   * @param {string} [endDate]
   * @returns {Promise<ExpenseSummary>}
   */
  async getExpenseSummary(startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const data = await api.get(`/api/admin/expenses/analytics/summary?${params}`);
    assertShape(data, ['total_expenses'], 'expenseService.getExpenseSummary');
    return data;
  },

  /**
   * @param {number} year
   * @param {number} [month]
   * @param {number} [quarter]
   * @returns {Promise<TaxReport>}
   */
  async getTaxReport(year, month = null, quarter = null) {
    const params = new URLSearchParams({ year: String(year) });
    if (month) params.append('month', String(month));
    if (quarter) params.append('quarter', String(quarter));

    const data = await api.get(`/api/admin/expenses/analytics/tax-report?${params}`);
    // Backend response has no top-level `year` key — it returns `period`
    // (e.g. "2026", "2026-Q1", "2026-09") plus totals. assertShape(['year'])
    // would have thrown on every valid response.
    assertShape(data, ['period'], 'expenseService.getTaxReport');
    return data;
  },
};

export default expenseService;
